/**
 * Utility functions to detect and work with connected wallets
 */

import { updateWalletConnection } from '../services/userProfileService';

export interface WalletInfo {
    address: string;
    provider: 'phantom' | 'solflare' | 'unknown';
    publicKey: any;
}

/**
 * Detect currently connected wallet
 * @returns WalletInfo object or null if no wallet connected
 */
export const detectConnectedWallet = (): WalletInfo | null => {
    try {
        // Check Phantom wallet first
        if (window.solana?.isConnected && window.solana?.publicKey) {
            return {
                address: window.solana.publicKey.toString(),
                provider: 'phantom',
                publicKey: window.solana.publicKey
            };
        }

        // Check Solflare wallet next
        if (window.solflare?.isConnected && window.solflare?.publicKey) {
            return {
                address: window.solflare.publicKey.toString(),
                provider: 'solflare',
                publicKey: window.solflare.publicKey
            };
        }

        // No wallet connected
        return null;
    } catch (error) {
        console.error("Error detecting connected wallet:", error);
        return null;
    }
};

/**
 * Updates the user record when wallet connects
 */
export const handleWalletConnect = async () => {
    const wallet = detectConnectedWallet();

    if (wallet) {
        try {
            // Update user profile in database - this now uses adminSupabase
            await updateWalletConnection(wallet.address, wallet.provider);
            console.log(`Wallet connection updated for ${wallet.address}`);

            // Dispatch custom event for other components to respond
            const event = new CustomEvent('ekko:wallet-connected', {
                detail: wallet
            });
            document.dispatchEvent(event);

            return wallet;
        } catch (error) {
            console.error("Error handling wallet connect:", error);
        }
    }

    return null;
};

/**
 * Add global listeners for wallet state changes
 */
export const setupGlobalWalletListeners = () => {
    const handlePhantomConnect = async () => {
        if (window.solana?.isConnected && window.solana?.publicKey) {
            try {
                const wallet = {
                    address: window.solana.publicKey.toString(),
                    provider: 'phantom' as const,
                    publicKey: window.solana.publicKey
                };
                await updateWalletConnection(wallet.address, wallet.provider);

                const event = new CustomEvent('ekko:wallet-connected', {
                    detail: wallet
                });
                document.dispatchEvent(event);
            } catch (error) {
                console.error("Error handling Phantom connect:", error);
            }
        }
    };

    const handleSolflareConnect = async () => {
        if (window.solflare?.isConnected && window.solflare?.publicKey) {
            try {
                const wallet = {
                    address: window.solflare.publicKey.toString(),
                    provider: 'solflare' as const,
                    publicKey: window.solflare.publicKey
                };
                await updateWalletConnection(wallet.address, wallet.provider);

                const event = new CustomEvent('ekko:wallet-connected', {
                    detail: wallet
                });
                document.dispatchEvent(event);
            } catch (error) {
                console.error("Error handling Solflare connect:", error);
            }
        }
    };

    const handleWalletDisconnect = () => {
        const event = new CustomEvent('ekko:wallet-disconnected');
        document.dispatchEvent(event);
    };

    // Add listeners for Phantom
    if (window.solana) {
        window.solana.on('connect', handlePhantomConnect);
        window.solana.on('disconnect', handleWalletDisconnect);
        window.solana.on('accountChanged', handlePhantomConnect);
    }

    // Add listeners for Solflare
    if (window.solflare) {
        window.solflare.on('connect', handleSolflareConnect);
        window.solflare.on('disconnect', handleWalletDisconnect);
        window.solflare.on('accountChanged', handleSolflareConnect);
    }

    // Check for existing connections
    const wallet = detectConnectedWallet();
    if (wallet) {
        updateWalletConnection(wallet.address, wallet.provider).catch(
            err => console.error("Error updating initial wallet connection:", err)
        );
    }

    return () => {
        // Cleanup listeners
        if (window.solana) {
            window.solana.removeListener('connect', handlePhantomConnect);
            window.solana.removeListener('disconnect', handleWalletDisconnect);
            window.solana.removeListener('accountChanged', handlePhantomConnect);
        }

        if (window.solflare) {
            window.solflare.removeListener('connect', handleSolflareConnect);
            window.solflare.removeListener('disconnect', handleWalletDisconnect);
            window.solflare.removeListener('accountChanged', handleSolflareConnect);
        }
    };
};
